import React, { useState, useEffect } from 'react';
import {
  Plus,
  Tag,
  Search,
  Filter,
  Edit,
  Trash2,
  Eye,
  Users,
  Building2,
  Calendar,
  Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { toast } from '../../hooks/use-toast';
import ConfirmDialog from '../../components/ui/confirm-dialog';
import { useConfirm } from '../../components/ui/use-confirm';

import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
// (deduped imports)

import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { tagsAPI, usersAPI } from '../../services/api';
import { cn } from '../../lib/utils';
// (deduped imports)

const tagColors = [
  { name: 'Red', value: 'red', bg: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
  { name: 'Blue', value: 'blue', bg: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  { name: 'Green', value: 'green', bg: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  { name: 'Purple', value: 'purple', bg: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' },
  { name: 'Amber', value: 'amber', bg: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  

  { name: 'Cyan', value: 'cyan', bg: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400' },
  { name: 'Pink', value: 'pink', bg: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400' },
  { name: 'Orange', value: 'orange', bg: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },

];

const TagsManagementPage = () => {
  const [tags, setTags] = useState([]);
  const { confirm, dialogProps } = useConfirm();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [colorFilter, setColorFilter] = useState('all');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [newTag, setNewTag] = useState({ name: '', color: 'blue' });

  // Fetch data on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [tagsRes, usersRes] = await Promise.all([
          tagsAPI.list(),
          usersAPI.list()
        ]);
        setTags(tagsRes.data || []);
        setUsers(usersRes.data || []);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getTagColor = (color) => {
    return tagColors.find(c => c.value === color)?.bg || 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300';
  };

  const getCreatorName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user?.name || 'Admin';
  };

  // Calculate this month's tags
  const thisMonthTags = tags.filter(tag => {
    if (!tag.created_at) return false;
    const tagDate = new Date(tag.created_at);
    const now = new Date();
    return tagDate.getMonth() === now.getMonth() && tagDate.getFullYear() === now.getFullYear();
  }).length;

  const handleCreateTag = async () => {
    try {
      const response = await tagsAPI.create({
        name: newTag.name,
        color: newTag.color
      });
      setTags(prev => [...prev, response.data]);
      setIsCreateOpen(false);
      setNewTag({ name: '', color: 'blue' });
    } catch (error) {
      console.error('Failed to create tag:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to create tag',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  const handleUpdateTag = async () => {
    if (!editingTag) return;
    try {
      await tagsAPI.update(editingTag.id, {
        name: editingTag.name,
        color: editingTag.color
      });
      setTags(prev => prev.map(t => t.id === editingTag.id ? editingTag : t));
      setEditingTag(null);
    } catch (error) {
      console.error('Failed to update tag:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to update tag',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  const handleDeleteTag = async (tagId) => {
    const ok = await confirm({
      title: 'Delete tag?',
      description: 'This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      destructive: true
    });
    if (!ok) return;
    try {
      await tagsAPI.delete(tagId);
      setTags(prev => prev.filter(t => t.id !== tagId));
    } catch (error) {
      console.error('Failed to delete tag:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to delete tag',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  const filteredTags = tags.filter(tag => {
    const matchesSearch = (tag.name || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesColor = colorFilter === 'all' || tag.color === colorFilter;
    return matchesSearch && matchesColor;
  });

  // Count tags by color
  const colorCounts = tagColors.reduce((acc, color) => {
    acc[color.value] = tags.filter(t => t.color === color.value).length;
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        <span className="ml-2 text-slate-500">Loading tags...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ConfirmDialog
        open={dialogProps.open}
        onOpenChange={dialogProps.onOpenChange}
        title={dialogProps.title}
        description={dialogProps.description}
        confirmText={dialogProps.confirmText}
        cancelText={dialogProps.cancelText}
        destructive={dialogProps.destructive}
        onConfirm={dialogProps.onConfirm}
        onCancel={dialogProps.onCancel}
      />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">Tags Management</h1>
          <p className="text-slate-500 dark:text-slate-400">Create and manage tags for leads, demos, and deals</p>
        </div>
        <Button className="bg-slate-800 hover:bg-slate-900" onClick={() => setIsCreateOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Create Tag
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Tag className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Total Tags</p>
                <p className="text-2xl font-bold text-slate-800 dark:text-slate-100">{tags.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                <Users className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Creators</p>
                <p className="text-2xl font-bold text-slate-800 dark:text-slate-100">
                  {new Set(tags.map(t => t.created_by).filter(Boolean)).size || 1}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <Building2 className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Colors Used</p>
                <p className="text-2xl font-bold text-slate-800 dark:text-slate-100">
                  {Object.values(colorCounts).filter(c => c > 0).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                <Calendar className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">This Month</p>
                <p className="text-2xl font-bold text-slate-800 dark:text-slate-100">{thisMonthTags}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search tags..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-white dark:bg-slate-800"
          />
        </div>
        <Select value={colorFilter} onValueChange={setColorFilter}>
          <SelectTrigger className="w-[180px] bg-white dark:bg-slate-800">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="All Colors" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Colors</SelectItem>
            {tagColors.map(color => (
              <SelectItem key={color.value} value={color.value}>
                <span className="flex items-center gap-2">
                  <span className={cn('w-3 h-3 rounded-full', color.bg.split(' ')[0])}></span>
                  {color.name} ({colorCounts[color.value] || 0})
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Tags Table */}
      <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 dark:bg-slate-900">
                <TableHead className="font-semibold">Tag</TableHead>
                <TableHead className="font-semibold">Color</TableHead>
                <TableHead className="font-semibold">Created By</TableHead>
                <TableHead className="font-semibold">Created At</TableHead>
                <TableHead className="w-[120px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTags.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                    {tags.length === 0 ? 'No tags created yet. Click "Create Tag" to add one.' : 'No tags match your search.'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredTags.map((tag) => (
                  <TableRow key={tag.id} className="hover:bg-slate-50 dark:hover:bg-slate-700">
                    <TableCell>
                      <Badge className={getTagColor(tag.color)}>
                        <Tag className="w-3 h-3 mr-1" />
                        {tag.name}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="capitalize dark:text-slate-300">{tag.color}</span>
                    </TableCell>
                    <TableCell className="dark:text-slate-300">
                      {getCreatorName(tag.created_by)}
                    </TableCell>
                    <TableCell className="dark:text-slate-300">
                      {tag.created_at ? new Date(tag.created_at).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => setEditingTag({...tag})}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-red-600 hover:text-red-700"
                          onClick={() => handleDeleteTag(tag.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Tag Modal */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Create New Tag</DialogTitle>
            <DialogDescription>Create a tag to categorize leads, demos, and deals.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Tag Name *</Label>
              <Input
                value={newTag.name}
                onChange={(e) => setNewTag({...newTag, name: e.target.value})}
                placeholder="e.g., High Priority"
              />
            </div>
            <div className="grid gap-2">
              <Label>Color</Label>
              <div className="flex flex-wrap gap-2">
                {tagColors.map(color => (
                  <button
                    key={color.value}
                    onClick={() => setNewTag({...newTag, color: color.value})}
                    className={cn(
                      'px-3 py-1.5 rounded-full text-sm font-medium transition-all',
                      color.bg,
                      newTag.color === color.value && 'ring-2 ring-offset-2 ring-slate-400'
                    )}
                  >
                    {color.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateTag} disabled={!newTag.name} className="bg-slate-800 hover:bg-slate-900">
              Create Tag
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Tag Modal */}
      <Dialog open={!!editingTag} onOpenChange={() => setEditingTag(null)}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Edit Tag</DialogTitle>
            <DialogDescription>Update the tag name or color.</DialogDescription>
          </DialogHeader>
          {editingTag && (
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label>Tag Name *</Label>
                <Input
                  value={editingTag.name}
                  onChange={(e) => setEditingTag({...editingTag, name: e.target.value})}
                  placeholder="e.g., High Priority"
                />
              </div>
              <div className="grid gap-2">
                <Label>Color</Label>
                <div className="flex flex-wrap gap-2">
                  {tagColors.map(color => (
                    <button
                      key={color.value}
                      onClick={() => setEditingTag({...editingTag, color: color.value})}
                      className={cn(
                        'px-3 py-1.5 rounded-full text-sm font-medium transition-all',
                        color.bg,
                        editingTag.color === color.value && 'ring-2 ring-offset-2 ring-slate-400'
                      )}
                    >
                      {color.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingTag(null)}>Cancel</Button>
            <Button onClick={handleUpdateTag} disabled={!editingTag?.name} className="bg-slate-800 hover:bg-slate-900">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TagsManagementPage;
